// Pure helpers for the "Good numbers" and "Not texted" tabs of the SMS Status
// modal (components/sms/NumberListPanels.js). Good numbers come from
// public.sms_number_health; "not texted" is every patient phone in a
// provider's "notes-c" that sms_number_health has no row for, which is exactly
// when PhoneStatusBadge says "Not texted yet".

import { smsE164 } from "./badNumbers";

// A dialable number: 11 to 15 digits once normalized. Rules out blanks,
// partial numbers and several numbers typed into one field.
export const dialableE164 = (raw) => {
  const e164 = smsE164(raw);
  return e164.length >= 12 && e164.length <= 16 ? e164 : "";
};

// Every dialable number on a "notes-c" row: the cell (tel_num) and, when the
// schema has the column, the home phone. Each once.
export const patientDialableNumbers = (p) =>
  [...new Set([p?.tel_num, p?.home_phone].map(dialableE164).filter(Boolean))];

const visitTime = (v) => {
  const t = v ? new Date(v).getTime() : NaN;
  return Number.isFinite(t) ? t : 0;
};

/**
 * One row per (schema, number) from "notes-c" rows, skipping numbers in
 * `texted` (a Set of E.164 numbers). Each row lists the distinct patients on
 * that number, latest visit first; rows are sorted by latest visit.
 * patientsBySchema: { [schema]: [{ id, patient_name, tel_num, home_phone?, date_ }] }
 * A patient's home phone is a number of its own (texts go there when the
 * cell is bad), so it is listed too.
 */
export const notTextedNumbers = (patientsBySchema, texted) => {
  const byKey = new Map();
  Object.entries(patientsBySchema).forEach(([schema, patients]) => {
    (patients || []).forEach((p) => {
      const name = String(p.patient_name || "").trim();
      patientDialableNumbers(p).forEach((phone) => {
        if (texted.has(phone)) return;
        const key = `${schema}:${phone}`;
        if (!byKey.has(key)) byKey.set(key, { schema_name: schema, phone_number: phone, byName: new Map() });
        const who = name.toLowerCase();
        const seen = byKey.get(key).byName.get(who);
        if (!seen || visitTime(p.date_) > visitTime(seen.lastVisit)) {
          byKey.get(key).byName.set(who, { id: String(p.id), name, lastVisit: p.date_ || null });
        }
      });
    });
  });
  return [...byKey.values()]
    .map(({ schema_name, phone_number, byName }) => {
      const patients = [...byName.values()].sort((a, b) => visitTime(b.lastVisit) - visitTime(a.lastVisit));
      return {
        schema_name,
        phone_number,
        patients,
        patient_names: patients.map((p) => p.name).filter(Boolean),
        last_visit: patients[0]?.lastVisit || null,
      };
    })
    .sort((a, b) => visitTime(b.last_visit) - visitTime(a.last_visit) || a.phone_number.localeCompare(b.phone_number));
};

/** Rows matching a patient name or number fragment, and the provider if one is picked. */
export const filterNumberRows = (rows, { search = "", provider = "" } = {}) => {
  const needle = search.trim().toLowerCase();
  const digits = needle.replace(/\D/g, "");
  return rows.filter((row) => {
    if (provider && row.schema_name !== provider) return false;
    if (!needle) return true;
    if (digits && String(row.phone_number || "").replace(/\D/g, "").includes(digits)) return true;
    return (row.patient_names || []).some((n) => n.toLowerCase().includes(needle));
  });
};
